import { OrderStatusType, ClientIDType, OrderFlagType } from '../../types';
import { I18n, Store } from '../../wrappers';
import { Order, CashRegister, OrderItem, Shift, TableSession } from '../../models';
import { TenantService, TableSessionService } from '../../services';
import { Money, vatExemptNote } from '../../utilities';
import { Printable, receiptLogoFor } from '../../print';
import { dateFormatter } from '../../utilities/dateFormatter';
import CryptoService from '../../services/CryptoService';
import moment from 'moment-timezone';
/**
 * The IIC's dateTimeCreated (and the QR's crtd) are composed in the tax
 * authority's zone, never the device's: aria-api signs the same fingerprint
 * pinned to Europe/Podgorica and derives the registered IIC from it — a
 * terminal on another zone would otherwise print an IIC that is not the
 * registered one.
 */
const FISCAL_TIMEZONE = 'Europe/Podgorica';
export class OrderLocalizationMe {
    order;
    constructor(order) {
        this.order = order;
    }
    static fiscalStatuses() {
        return [OrderStatusType.paid, OrderStatusType.advance, OrderStatusType.unpaid, OrderStatusType.summary, OrderStatusType.creditNote];
    }
    shouldHaveNumber() {
        // External documents carry no receipt number anywhere — the server
        // nulls it on create, so numbering one locally would diverge.
        if (this.order.isExternal())
            return false;
        return this.order.status != OrderStatusType.proforma;
    }
    shouldPrintReceipt() {
        return !this.order.flagsInclude('closeAdvance');
    }
    /**
     * The EFI verify URL the receipt's QR carries. `venue` is the receipt's own venue when the
     * caller has it (SAP renders a client's receipt without a logged-in tenant), else the
     * logged-in tenant's; the verify page and the software code come from the fiscal app config.
     * Without a venue or the fiscal config there is no QR.
     */
    async generateQRCodeString(venue) {
        const resolvedVenue = venue ?? Store.get('tenant')?.venue;
        const fiscal = Store.get('appConfig')?.fiscal;
        let cashRegister = CashRegister.find(this.order.cash_register_id);
        if (!resolvedVenue || !fiscal) {
            return '';
        }
        const baseUrl = fiscal.efiQrCodeUrl;
        const iic = await this.getIic();
        // The verify URL is meaningful only with the IIC it verifies — without
        // one, a printed QR would resolve to "not found" on a legal receipt.
        if (!iic) {
            return '';
        }
        const tin = resolvedVenue.tax_id_number || '';
        const crtd = moment.utc(this.order.last_activity_at).tz(FISCAL_TIMEZONE).format();
        const ord = this.order.number;
        const bu = resolvedVenue.hash || '';
        const cr = cashRegister?.hash || '';
        const sw = fiscal.softwareCode;
        const prc = this.order.getToPay().toFormat('0.00');
        return `${baseUrl}?iic=${iic}&tin=${tin}&crtd=${crtd}&ord=${ord}&bu=${bu}&cr=${cr}&sw=${sw}&prc=${prc}`;
    }
    async getIic(iicSignature) {
        // A stored IIC is served as-is EXCEPT on a proforma: a proforma is
        // never fiscalized, so any value present was memoized by the
        // unguarded derivation below and reading it back would resurrect it.
        // (Orders can legitimately hold an IIC without a local number —
        // incoming invoices imported from a supplier's EFI verify QR — so
        // only the proforma case is excluded here.)
        if (this.order.iic && !this.order.isProforma()) {
            return this.order.iic;
        }
        // Nothing stored worth serving. Deriving needs a number: the EFI
        // fingerprint embeds invoiceNumber, so an unnumbered document would
        // compose "|null", memoize a meaningless code on the order, and
        // print a verify QR that resolves to "not found".
        if (!this.order.number) {
            return '';
        }
        if (!iicSignature) {
            iicSignature = '';
            let tenant = Store.get('tenant');
            if (tenant) {
                let cashRegister = CashRegister.find(this.order.cash_register_id);
                let iic = '';
                // issuerTIN
                iic += tenant?.venue?.tax_id_number || '';
                // dateTimeCreated
                iic += '|' + moment.utc(this.order.last_activity_at).tz(FISCAL_TIMEZONE).format();
                // invoiceNumber
                iic += '|' + this.order.number;
                // busiUnitCode
                iic += '|' + tenant?.venue?.hash || '';
                // tcrCode
                iic += '|' + cashRegister?.hash || '';
                // softCode
                iic += '|' + Store.get('appConfig').fiscal.softwareCode;
                // totalPrice
                iic += '|' + this.order.getToPay().toFormat('0.00');
                const pkey = await TenantService.getPkey();
                if (pkey) {
                    iicSignature = CryptoService.signMessage(iic, pkey);
                }
            }
        }
        if (!iicSignature)
            return '';
        this.order.iic = CryptoService.getIIC(iicSignature);
        return this.order.iic;
    }
    getTransactionDate() {
        if (this.order.start_date && this.order.end_date)
            return moment(this.order.end_date, 'YYYY-MM-DD');
        if (this.order.tax_period)
            return moment(this.order.tax_period, 'MM/YYYY').endOf('month');
        return this.order.getLastActivityAt();
    }
    async getUnpaidOrderPrintable(options, venue) {
        const pr = new Printable();
        const { formatDateTimeHuman } = dateFormatter();
        const { printingSector } = options;
        const curShift = Shift.current();
        const curShiftOrders = curShift ? curShift.getOrders() : [];
        const orderNumber = curShiftOrders.length + 1;
        // Prefer the stable callout code stored at fire time; fall back to the
        // live shift counter for historical orders issued before callout codes.
        const calloutCode = this.order.getFlag(OrderFlagType.calloutCode)?.[0];
        const displayCode = calloutCode || String(orderNumber);
        pr.l();
        const isRefunded = this.order.canceled() || this.order.getToPay().toUnit() < 0;
        if (isRefunded) {
            pr.l('L', I18n.t('general.order_storno').toUpperCase(), { align: 'C', scale: '3,3' });
            pr.l();
        }
        if (printingSector) {
            pr.l('L', `${displayCode} # ${I18n.t('products.printing_sector.' + printingSector)}`, { align: 'C', scale: '2,2' });
            pr.l();
        }
        pr.l('D', '', { delimiter: '-' });
        if (this.order.isDelivery()) {
            // The banner above carries the callout; this line only names the
            // platform so the kitchen knows the order leaves the venue.
            const platformLabel = this.order.source.charAt(0).toUpperCase() + this.order.source.slice(1);
            pr.l('L', platformLabel, { scale: '2,2' });
        }
        else {
            const table = this.order.getTable();
            if (table) {
                const sector = this.order.getSector();
                const tableLabel = table.name + (sector ? ` (${sector.name})` : '');
                const guestId = this.order.getGuestId();
                const guestLabel = guestId !== null ? ` / G${guestId + 1}` : '';
                // A guest-placed round: the kitchen sees the table like any
                // waiter round, marked so staff know nobody keyed it in.
                const sourceLabel = this.order.isQrOrder() ? ' / QR' : '';
                pr.l('L', `${I18n.t('general.table')}: ${tableLabel}${guestLabel}${sourceLabel}`);
            }
        }
        const lastActivityAt = this.order.getLastActivityAt();
        pr.l('L', `${I18n.t('general.issued_at')}: ${lastActivityAt ? formatDateTimeHuman(lastActivityAt) : ''}`);
        pr.l('B', `${I18n.t('general.issued_by')}:`, this.order.getUser()?.name || '');
        pr.l('D', '', { delimiter: '-' });
        // Product Items
        let productItems = this.order.items.map((item, index) => {
            item.index = index;
            return item;
        });
        // Item separators
        const separators = this.order.getItemSeparators();
        if (printingSector && printingSector !== 'order') {
            productItems = productItems.filter((item) => {
                const product = item.getProduct();
                return !product || product.printing_sector === printingSector;
            });
        }
        pr.l();
        productItems.forEach((item, index) => {
            if (index !== 0 && separators.length && separators[0] <= item.index) {
                pr.l('D', '', { delimiter: '-' });
                pr.l();
                separators.shift();
            }
            const count = isRefunded ? -1 * Math.abs(item.getCount()) : item.getCount();
            pr.l('L', `${count} x ${item.name}`, { scale: '2,2' });
            if (item.comments.length) {
                pr.l();
            }
            item.comments.forEach((comment) => {
                pr.l('L', `- ${comment}`);
            });
            pr.l();
        });
        return pr;
    }
    async getOrderPrintable(options, venue) {
        const priceFormat = '0,0.00';
        const pr = new Printable();
        const { formatDateTime, formatDate } = dateFormatter();
        const { isCopy } = options;
        let client = this.order.getClient();
        const tenant = Store.get('tenant');
        const resolvedVenue = venue || tenant?.venue;
        if (!resolvedVenue)
            return pr;
        const receiptNote = resolvedVenue?.receipt_note?.split('|')[0] || '';
        const receiptHeaderText = resolvedVenue?.receipt_note?.split('|')[1] || '';
        const receiptLogo = receiptLogoFor(resolvedVenue);
        if (receiptLogo)
            pr.l('LOGO', receiptLogo);
        // Receipt Header
        pr.l('D', '', { delimiter: '=' });
        pr.l('L', resolvedVenue.company_name, { align: 'C' });
        pr.l('L', resolvedVenue.name, { align: 'C' });
        if (resolvedVenue.tax_id_number && resolvedVenue.tax_id_number.length !== 13) {
            pr.l('L', `${I18n.t('general.tax_id_number')}: ${resolvedVenue.tax_id_number}`, { align: 'C' });
        }
        pr.l('L', resolvedVenue.address, { align: 'C' });
        if (receiptHeaderText) {
            receiptHeaderText.split('\n').forEach(line => pr.l('L', line, { align: 'C' }));
        }
        if (this.order.isProforma()) {
            pr.l('L', I18n.t('general.proforma'), { align: 'C' });
        }
        else if (isCopy && !resolvedVenue?.settings?.hide_receipt_copy_label) {
            pr.l('L', I18n.t('general.receipt_copy'), { align: 'C' });
        }
        pr.l('D', '', { delimiter: '=' });
        // Client
        if (client && client.name) {
            pr.l('L', `${I18n.t('general.buyer')}: ${client.name}`);
            if (client.address) {
                pr.l('L', `${client.address}${client.city ? (', ' + client.city) : ''}${client.country ? (', ' + client.country) : ''}`);
            }
            let taxIdNumberPrinted = false;
            if ((client.country && client.country !== 'MNE') && client.id_number) {
                const idType = I18n.t(`general.${client.id_type}`);
                pr.l('L', `${idType}: ${client.id_number}`);
                taxIdNumberPrinted = client.id_type === ClientIDType.tax_id_number;
            }
            if (!taxIdNumberPrinted && client.tax_id_number) {
                pr.l('L', `${I18n.t('general.tax_id_number')}: ${client.tax_id_number}`);
            }
            if (client.tax_id_card_number) {
                pr.l('L', `${I18n.t('general.tax_id_card_number')}: ${client.tax_id_card_number}`);
            }
            pr.l('D', '', { delimiter: '-' });
        }
        // Receipt Subheader
        if (this.order.internal_number) {
            pr.l('B', !this.order.isProforma() ? I18n.t('invoice.number') : I18n.t('invoice.proforma.number'), this.order.internal_number);
        }
        if (this.order.number) {
            pr.l('L', `${I18n.t('general.receipt_number_serial')}: ${this.order.number}`);
            pr.l('B', `${I18n.t('general.receipt_number')}:`, this.order.getFullNumber(resolvedVenue));
        }
        const lastActivityAt = this.order.getLastActivityAt();
        pr.l('L', `${I18n.t('general.issued_at')}: ${lastActivityAt ? formatDateTime(lastActivityAt) : ''}`);
        let transactionDate = null;
        if (this.order.start_date && this.order.end_date) {
            transactionDate = moment(this.order.end_date, 'YYYY-MM-DD');
        }
        else if (this.order.tax_period) {
            transactionDate = moment(this.order.tax_period, 'MM/YYYY').endOf('month');
        }
        else {
            transactionDate = lastActivityAt || moment();
        }
        pr.l('L', `${I18n.t('general.transaction_date')}: ${formatDate(transactionDate)}`);
        if (!this.order.isIncoming()) {
            pr.l('L', `${I18n.t('general.operator')}: ${this.order.getUser()?.name || ''} / ${this.order.getUser()?.hash || ''}`);
        }
        if (this.order.paymentType() && !this.order.isProforma()) {
            pr.l('L', `${I18n.t('general.paid_by')}: ${this.order.paymentType()}`);
        }
        if (this.order.isSummary()) {
            pr.l('L', `${I18n.t('general.receipt_type')}: ${I18n.t('general.receipt_summary')}`);
        }
        else if (this.order.isAdvance()) {
            pr.l('L', `${I18n.t('general.receipt_type')}: ${I18n.t('general.advance')}`);
        }
        if (this.order.getTable()) {
            pr.l('L', `${I18n.t('general.table')}: ${this.order.getTable()?.name || ''}`);
        }
        // Product Items
        let productItems = this.order.items;
        if (this.order.isSummary()) {
            productItems = OrderItem.merge(this.order.paysForOrders().flatMap(o => o.items), this.order);
            const isRefund = this.order.getToPay().toUnit() < 0;
            if (isRefund) {
                productItems = productItems.map(item => {
                    item.count = Order.round(-1 * item.getCount(), 3).toFixed(3);
                    return item;
                });
            }
        }
        if (productItems.length) {
            pr.l('D', '', { delimiter: '-' });
            pr.l('S', `${I18n.t('general.item')} (${I18n.t('tax_rates.rate')})`, I18n.t('general.price'));
            pr.l('D', '', { delimiter: '-' });
        }
        const itemCommentsOnFiscalReceipts = !!venue?.settings?.item_comments_on_fiscal_receipts;
        productItems.forEach((item) => {
            const count = item.getCount();
            const name = item.name;
            const unitPrice = item.getUnitPriceAfterTax().toFormat(priceFormat);
            const price = item.getPrice().toFormat(priceFormat);
            const discountPercent = item.getDiscountPercent();
            const discountAmount = item.getDiscountAmount().multiply(-1).toFormat(priceFormat);
            const toPay = item.getToPay().toFormat(priceFormat);
            pr.l('L', `${name} (${item.getTaxRate()}%)`);
            if (itemCommentsOnFiscalReceipts && item.comments.length) {
                item.comments.join('\n').split('\n').forEach((line) => pr.l('L', line));
            }
            pr.l('S', `   ${count} x ${unitPrice}`, price);
            if (discountPercent > 0) {
                pr.l('S', `   ${I18n.t('general.discount')} (${discountPercent}%)`, discountAmount);
                pr.l('S', `   ${I18n.t('general.with_discount')}`, toPay);
            }
        });
        // Receipt items
        const receiptItems = this.order.isSummary() ? this.order.items : [];
        if (receiptItems.length) {
            pr.l('D', '', { delimiter: '-' });
            pr.l('S', I18n.t('general.receipt'), I18n.t('general.price'));
            pr.l('D', '', { delimiter: '-' });
        }
        receiptItems.forEach((item) => {
            const count = item.getCount();
            const name = item.name;
            const unitPrice = item.getUnitPriceAfterTax().toFormat(priceFormat);
            const toPay = item.getToPay().toFormat(priceFormat);
            pr.l('L', name);
            pr.l('S', `   ${count} x ${unitPrice}`, toPay);
        });
        const taxes = this.order.taxes.filter((taxItem) => !!taxItem.id || (!taxItem.id && taxItem.label.includes('VAT_')));
        if (resolvedVenue.taxes_enabled && taxes.length) {
            pr.l('D', '', { delimiter: '-' });
            // Taxes and Total Tax
            taxes.filter((taxItem) => !taxItem.id).forEach((taxItem) => {
                pr.l('S', `${I18n.t('order.exempt_from_tax')} ${taxItem.label.replace('VAT_', '')}:`, Money.parse(taxItem.price_before_tax).toFormat(priceFormat));
            });
            taxes.filter((taxItem) => !!taxItem.id).forEach((taxItem) => {
                pr.l('S', `${taxItem.label} ${I18n.t('general.price_pre_tax').toLowerCase()}:`, Money.parse(taxItem.price_before_tax).toFormat(priceFormat));
                pr.l('S', `${taxItem.label} (${parseFloat(taxItem.rate)}%):`, Money.parse(taxItem.value).toFormat(priceFormat));
            });
        }
        pr.l('D', '', { delimiter: '-' });
        // Tax Summary
        const summary = this.getOrderSummary();
        const paymentTypes = this.order.getPayments();
        if (resolvedVenue.taxes_enabled) {
            if (summary.exemptFromTax && summary.exemptFromTax.toUnit() > 0) {
                pr.l('S', `${I18n.t('order.exempt_from_tax')}:`, summary.exemptFromTax.toFormat(priceFormat));
            }
            if (summary.priceBeforeTax) {
                pr.l('S', `${I18n.t('general.price_pre_tax_total')}:`, summary.priceBeforeTax.toFormat(priceFormat));
            }
            pr.l('S', `${I18n.t('general.total_tax')}:`, this.order.getTax().toFormat(priceFormat));
            pr.l('D', '', { delimiter: '-' });
        }
        // Price and Discount
        if (summary.price) {
            pr.l('S', `${I18n.t('general.total')}:`, summary.price.toFormat(priceFormat));
        }
        if (summary.discountAmount) {
            pr.l('S', `${I18n.t('general.discount')}:`, summary.discountAmount.toFormat(priceFormat));
        }
        if (summary.toPay) {
            pr.l('D', '', { delimiter: '-' });
            pr.l('S', `${I18n.t('general.to_pay').toUpperCase()}:`, summary.toPay.toFormat(priceFormat), { scale: '2,2' });
        }
        paymentTypes.forEach(type => {
            pr.l('S', `${type.display}:`, Money.parse(type.amount).toFormat(priceFormat));
        });
        let note = this.order.note || '';
        const vatNote = vatExemptNote(resolvedVenue, '* ' + I18n.t('order.exempt_from_tax_comment'));
        if (vatNote) {
            note = vatNote + '\n\n' + note;
        }
        if (note) {
            pr.l('D', '', { delimiter: '-' });
            note.split('\n').forEach(line => pr.l('L', line));
        }
        pr.l('D', '', { delimiter: '-' });
        if (this.order.getPaidInvoice().toUnit() > 0) {
            pr.l('L', I18n.t('general.buyer'), { align: 'C' });
            pr.l();
            pr.l();
            pr.l('D', '', { delimiter: '-' });
        }
        // Fiscal Codes
        if (!this.order.isProforma()) {
            if (this.order.correctsOrders().length) {
                const refIIC = await this.order.correctsOrders()[0].getIic();
                if (refIIC) {
                    pr.l('B', 'REF IKOF:', refIIC);
                }
            }
            const iic = await this.getIic();
            if (iic) {
                pr.l('B', 'IKOF:', iic);
            }
            if (this.order.fiscal_code) {
                pr.l('B', 'JIKR:', this.order.fiscal_code);
            }
        }
        if (receiptNote) {
            pr.l('D', '', { delimiter: '-' });
            receiptNote.split('\n').forEach(line => pr.l('L', line));
            pr.l('D', '', { delimiter: '-' });
        }
        const QRCode = await this.generateQRCodeString(venue);
        if (QRCode) {
            pr.l('QR', QRCode, { size: '40mm' });
        }
        const domain = I18n.c('general.domain');
        if (domain) {
            pr.l('L', `www.${domain}`, { align: 'C' });
        }
        this.appendSessionPin(pr, options);
        return pr;
    }
    /**
     * Montenegro prints a fiscal receipt for every unpaid round
     * (order.unpaidIssuesReceipt) — that receipt is taken to the guest's
     * table, so it carries the QR-ordering session PIN at the bottom and
     * the automatic PIN modal is suppressed (see Order.vue issueOrder).
     * Only on the ORIGINAL print of unpaid (round) receipts while a session
     * is active in PIN mode — never on copies/reprints (the receipts page
     * passes isCopy), settlement receipts (the session is closing), in
     * 'open' security mode, or after the session closed.
     * No QR is printed: the fiscal verification QR above must remain the
     * only scannable code on the receipt; guests scan the table QR.
     */
    appendSessionPin(pr, options) {
        // Receipts also render in admin/SAP previews — a PIN embellishment
        // must never break a fiscal receipt, so any failure is a no-op.
        try {
            if (options.isCopy)
                return;
            if (!this.order.isUnpaid() || !this.order.table_id)
                return;
            if (!TableSessionService.isQrOrderingEnabled())
                return;
            const securityMode = Store.get('tenant')?.venue?.settings?.qr_ordering?.security_mode;
            if (securityMode === 'open')
                return;
            const session = TableSession.forTable(this.order.table_id);
            if (!session?.pin)
                return;
            pr.l('D', '', { delimiter: '-' });
            pr.l('L', I18n.t('qr_ordering.session_pin'), { align: 'C' });
            pr.l('L', session.pin.split('').join('  '), { align: 'C', scale: '2,2', style: { fontWeight: '700' } });
        }
        catch {
            // no-op: the receipt must print regardless
        }
    }
    getOrderSummary() {
        return {
            exemptFromTax: this.order.getExemptFromTax(),
            priceBeforeTax: this.order.getPriceBeforeTax(),
            price: this.order.getPrice(),
            discountAmount: this.order.getDiscountAmount(),
            toPay: this.order.getToPay(),
        };
    }
}
const OrderLocalizationMeStatic = OrderLocalizationMe;
//# sourceMappingURL=OrderLocalizationMe.js.map